import { useMemo } from "react";
import Scheduler from "../../components/scheduler/Scheduler";
import UiDialog, { UiDialogInputProps } from "../../components/ui-lib/UiDialog";
import { ViewModeSubstituteSelectionWeek } from "../../components/ui-lib/scheduler/viewModeSubstituteSelectionWeek";
import { generateComponentSchedule } from "../../helpers/utils/calendar.util";
import useSchedulerLessons from "../../hooks/useSchedulerLessons";
import { VacancyLesson } from "../../models/schedule/lesson";
import { Teacher } from "../../models/teacher/teacher";
import { Vacancy } from "../../models/vacancy/vacancy";
import Button from "../../components/ui-lib/buttons/Button";
import TeacherDetails from "../../components/teacher/TeacherDetails";

export type AcceptTeacherDialogResultProps = {
  teacher: Teacher;
  acceptedLessons?: VacancyLesson[];
  declinedLessons?: VacancyLesson[];
};

type AcceptTeacherApplicationDialogProps = {
  vacancy: Vacancy;
  teacherApplication: Teacher;
  onClose: (result: AcceptTeacherDialogResultProps | undefined) => void;
} & Omit<UiDialogInputProps, "title" | "children" | "onClose">;

function AcceptTeacherApplicationDialog({
  vacancy,
  teacherApplication,
  onClose,
  ...props
}: AcceptTeacherApplicationDialogProps) {
  const { lessons, updateLessons } = useSchedulerLessons(generateComponentSchedule(
    vacancy.schedule,
    new Date(vacancy.startDate),
    new Date(vacancy.endDate),
    vacancy.lessons,
  ));
  const readonly = useMemo(
    () =>
      !lessons
        .flat()
        .some((l) =>
          l.teacherApplications?.some(
            (application) => application.id === teacherApplication.id,
          ),
        ),
    [lessons, teacherApplication.id],
  );

  const _onClose = () => {
    onClose(undefined);
  };

  const onAccept = () => {
    onClose({
      teacher: teacherApplication,
      acceptedLessons: lessons.flat().filter(
        (lesson) =>
          lesson.isActive &&
          lesson.teacherApplications?.some(
            (application) => application.id === teacherApplication.id,
          ),
        // Can convert safely to VacancyLesson here because EmptyLessons cannot be activated, therefore all active lessons must be VacancyLessons
      ) as VacancyLesson[],
    });
  };

  const onDecline = () => {
    onClose({
      teacher: teacherApplication,
      declinedLessons: lessons.flat().filter(
        (lesson) =>
          lesson.teacherApplications?.some(
            (application) => application.id === teacherApplication.id,
          ),
        // Can convert safely to VacancyLesson here because EmptyLessons cannot be activated, therefore all active lessons must be VacancyLessons
      ) as VacancyLesson[],
    });
  };

  return (
    <>
      <UiDialog
        title={`${teacherApplication.fullName} Bewerbung`}
        onClose={_onClose}
        className="md:w-2/3 space-y-4"
        {...props}
      >
        <TeacherDetails teacher={teacherApplication} />
        <Scheduler
          className="w-full"
          lessons={lessons}
          viewMode={new ViewModeSubstituteSelectionWeek(teacherApplication.id)}
          startDate={new Date(vacancy.startDate)}
          duration={vacancy.schedule.duration}
          isReadOnly={false}
          setValue={updateLessons}
        ></Scheduler>
        {!readonly && (
          <div className="flex flex-col-reverse md:flex-row md:justify-end md:space-x-2">
            <Button
              theme="primary"
              type="submit"
              className="mt-2 md:mt-0"
              onClick={onDecline}
            >
              Bewerbung ablehnen
            </Button>
            <Button
              theme="success"
              type="submit"
              className=""
              onClick={onAccept}
            >
              Selektierte Lektionen akzeptieren
            </Button>
          </div>
        )}
      </UiDialog>
    </>
  );
}

export default AcceptTeacherApplicationDialog;
